import { Bot, PanelLeft } from 'lucide-react'
import { useDetectedShortcutOs } from '@/hooks/use-shortcut-os'
import { formatModShortcut } from '@/lib/keyboard-platform'
import {
  SHELL_COLUMN_HEADER_CLASS,
  SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS,
  SHELL_PANEL_TOGGLE_BASE_CLASS,
  SHELL_PANEL_TOGGLE_IDLE_CLASS,
  SHELL_PANEL_TOGGLE_PRESSED_CLASS,
} from '@/lib/shell-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type ShellPanelToggleSide = 'left' | 'agent'

type ShellPanelToggleProps = {
  side: ShellPanelToggleSide
  /** Docked column. Pressed wash while true. */
  open: boolean
  /** When set, the control stays visible and will not toggle. The string is the tooltip. */
  disabledReason?: string
  /**
   * Auto-hide pin. Labels are Pin sidebar / Unpin sidebar.
   * Open uses the selected rail-button wash. Auto-hide itself stays on.
   */
  pinMode?: boolean
  onToggle: () => void
  /**
   * slot: the h-10 cap of the icon rail or the agent strip.
   * inline: the far-right button in an open column header.
   */
  placement?: 'slot' | 'inline'
  testId?: string
}

/**
 * The only show/hide control for a shell column.
 * Left uses PanelLeft. Agent uses Bot. PanelRight stays the preview pin.
 * Gallery: /?test=maater-ui#panel-collapse
 */
export function ShellPanelToggle({
  side,
  open,
  disabledReason,
  pinMode = false,
  onToggle,
  placement = 'inline',
  testId,
}: ShellPanelToggleProps) {
  const os = useDetectedShortcutOs()
  const shortcut = formatModShortcut(os, side === 'left' ? 'b' : 'j')
  const Icon = side === 'left' ? PanelLeft : Bot
  const action =
    side === 'left'
      ? pinMode
        ? open
          ? 'Unpin sidebar'
          : 'Pin sidebar'
        : open
          ? 'Collapse sidebar'
          : 'Expand sidebar'
      : open
        ? 'Collapse agent'
        : 'Agent'
  const blocked = Boolean(disabledReason)

  return (
    <button
      type="button"
      data-testid={testId ?? (side === 'left' ? testIds.shellToggle.left : testIds.shellToggle.agent)}
      data-shell-toggle={side}
      aria-pressed={open}
      aria-disabled={blocked || undefined}
      title={blocked ? disabledReason : `${action} (${shortcut})`}
      className={cn(
        placement === 'slot'
          ? cn(
              SHELL_COLUMN_HEADER_CLASS,
              'flex w-full items-center justify-center border-b border-border',
            )
          : SHELL_PANEL_TOGGLE_BASE_CLASS,
        open
          ? cn(
              SHELL_PANEL_TOGGLE_PRESSED_CLASS,
              pinMode && SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS,
              pinMode && 'ring-inset',
            )
          : SHELL_PANEL_TOGGLE_IDLE_CLASS,
        blocked && 'cursor-not-allowed opacity-40',
      )}
      onClick={() => {
        if (blocked) return
        onToggle()
      }}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}
